import { MapaPreview } from '@/shared/ui/MapaPreview/MapaPreview'
import { useEffect, useMemo, useState } from 'react'

import type { MiTrabajo } from '@/lib/supabase/tipos-bd'
import { RelojDelTrabajo } from '@/modules/ejecucion/RelojDelTrabajo'
import {
  IconoAlmuerzo,
  IconoAvance,
  IconoCamara,
  IconoRuta,
  IconoTelefono,
} from '@/shared/ui/Iconos/Iconos'
import { HorarioObra } from '@/shared/ui/HorarioObra/HorarioObra'
import {
  FueraDeLaGeocerca,
  SinUbicacion,
  useMarcarLlegada,
  useMarcarSalida,
  usePedirInicioSinUbicacion,
  useReportarAvance,
  useSalirAAlmuerzo,
} from '@/modules/ejecucion/useVisitas'
import { useSolicitarMasTiempo } from '@/modules/precierre/usePrecierre'
import { ESTADOS_ORDEN_TRABAJO } from '@/shared/dominio/estados'
import { fechaHora, horaCorta } from '@/shared/utilidades/formato'
import { fechaCorta, tramoDeObra } from '@/shared/utilidades/jornada'

import { esNativo, fotoNativa } from './camara'
import { calentarUbicacion } from '@/lib/nativo/ubicacion'

import { estamparFoto, posicionParaSello } from './estampar'
import { direccionDesdeCoords, precargarGeocodificador } from '@/lib/geocodificacion/google'
import { useSesion } from '@/modules/auth/useSesion'
import { useMarcasEnCola } from './useColaDeAcciones'
import type { ItemSobrante } from '@/lib/supabase/tipos-bd'
import { ElegirSobrantes } from '@/modules/sobrantes/ElegirSobrantes'
import { cantidadLegible, useMaterialesDeLaOrden, useSobrantesDeLaOrden } from '@/modules/sobrantes/useSobrantes'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'
import { fotoDeHerramienta } from '@/lib/supabase/deposito'
import {
  useHerramientasDeLaOrden,
  useHerramientasDisponibles,
  useHerramientasRequeridas,
} from '@/modules/programacion/useHerramientas'
import { IconoHerramienta } from '@/shared/ui/Iconos/Iconos'
import estilos from './TarjetaDeCampo.module.css'

type Props = { trabajo: MiTrabajo }

/**
 * Una orden, como la ve el técnico parado en la calle.
 *
 * Se lee de arriba abajo en el orden en que le hace falta: dónde es, cómo
 * llegar, cuánto le queda, y el botón que corresponde AL MOMENTO EN QUE ESTÁ.
 * Nunca hay dos acciones compitiendo: si no ha llegado, solo puede llegar; si
 * ya llegó, solo puede terminar.
 *
 * El botón grande y abajo no es capricho: se toca con el pulgar de una mano,
 * que es la única que suele tener libre.
 */
/**
 * Quita las marcas de negrita que llegan en el texto.
 *
 * La descripción viene del Excel y del asistente de WhatsApp, y trae asteriscos
 * de markdown: «instalación **completa** del aire». Aquí nadie los interpreta,
 * así que el técnico leía los asteriscos tal cual —parece un error de la
 * aplicación y estorba justo en el texto que tiene que seguir al pie de la
 * letra—. No se convierten a negrita a propósito: mezclar formato en un texto
 * que también trae medidas y referencias acaba resaltando lo que no es.
 */
function sinMarcas(texto: string | null | undefined): string | null {
  if (!texto) return null
  return texto
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|\s)\*(\S[^*]*?)\*(?=\s|$)/g, '$1$2')
    .replace(/(^|\s)_(\S[^_]*?)_(?=\s|$)/g, '$1$2')
    .trim()
}

export function TarjetaDeCampo({ trabajo }: Props) {
  const { perfil } = useSesion()
  /*
   * ¿ESTÁ EN EL SITIO? LO QUE DIGA EL SERVIDOR… O LA COLA DEL TELÉFONO.
   *
   * `en_sitio` viene del servidor, así que sin señal nunca cambia: el
   * técnico marcaba «Llegué», quedaba guardado, y la tarjeta seguía
   * ofreciéndole «Llegué» y escondiéndole «Terminar» y «Reportar avances».
   * O sea: podía entrar a la obra pero no salir de ella. Se vio en el
   * barrido de botones, no leyendo el código.
   *
   * Si hay una llegada esperando en la cola, para esta pantalla el técnico
   * YA llegó. El servidor lo confirmará cuando pueda.
   */
  /*
   * Y LO MISMO CON EL ALMUERZO (0235). Manda la marca más reciente: la del
   * servidor, o la que espera señal si es posterior. Salió a almorzar sin
   * señal → está almorzando; marcó después la vuelta → ya volvió.
   */
  const marcas = useMarcasEnCola(trabajo.orden_trabajo_id)
  const ultimaEnCola = marcas
    .filter((m) => ['llegada', 'almuerzo', 'salida', 'avance'].includes(m.clase))
    .sort((a, b) => a.ocurridoEn.localeCompare(b.ocurridoEn))
    .pop()
  const enAlmuerzo = ultimaEnCola
    ? ultimaEnCola.clase === 'almuerzo'
    : Boolean(trabajo.almuerzo_desde)
  const almuerzoDesde =
    ultimaEnCola?.clase === 'almuerzo' ? ultimaEnCola.ocurridoEn : trabajo.almuerzo_desde
  const enSitio = ultimaEnCola ? ultimaEnCola.clase === 'llegada' : trabajo.en_sitio
  const yaAlmorzo = trabajo.almorzo_hoy || marcas.some((m) => m.clase === 'almuerzo')
  const puedeAlmorzar = enSitio && !yaAlmorzo && trabajo.estado === 'en_ejecucion'

  const llegar = useMarcarLlegada(trabajo.orden_trabajo_id)
  const salir = useMarcarSalida(trabajo.orden_trabajo_id)
  const avanzar = useReportarAvance(trabajo.orden_trabajo_id)
  const almorzar = useSalirAAlmuerzo(trabajo.orden_trabajo_id)
  const pedirPermiso = usePedirInicioSinUbicacion(trabajo.orden_trabajo_id)
  const pedirTiempo = useSolicitarMasTiempo()

  const [aviso, setAviso] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Terminar es un paso, no un botón: hay que dejar evidencia. Se piden fotos
  // y un comentario de lo hecho ANTES de marcar la salida.
  const [terminando, setTerminando] = useState(false)
  const [fotos, setFotos] = useState<File[]>([])
  const [comentarioSalida, setComentarioSalida] = useState('')
  // ¿Sobró material? Obligatorio al terminar (0310). null = sin contestar.
  // La lista se pide apenas se pinta la tarjeta: así queda guardada en el
  // teléfono para cuando termine en la obra sin señal.
  const [sobrantes, setSobrantes] = useState<ItemSobrante[] | null>(null)
  // 0312: si sobró, la foto del material que sobró (aparte de la del acta).
  const [fotosSobrante, setFotosSobrante] = useState<File[]>([])
  const materiales = useMaterialesDeLaOrden(
    trabajo.estado === 'en_ejecucion' ? trabajo.orden_trabajo_id : null,
  )
  // 0314: lo que la oficina le asignó del inventario de sobrantes.
  const deSobrantes = useSobrantesDeLaOrden(
    ['programada', 'en_ejecucion'].includes(trabajo.estado) ? trabajo.orden_trabajo_id : null,
  )
  const asignados = deSobrantes.data?.asignados ?? []

  // 0318: Las herramientas que lleva o necesita esta orden
  const { yaTiene: herramientasAsignadas } = useHerramientasDeLaOrden(trabajo.orden_trabajo_id)
  const { requeridas: herramientasRequeridas } = useHerramientasRequeridas(trabajo.orden_trabajo_id)
  const { herramientas: catalogoHerramientas } = useHerramientasDisponibles()

  const listaHerramientasTrabajo = useMemo(() => {
    const mapa =
      Object.keys(herramientasAsignadas).length > 0
        ? herramientasAsignadas
        : herramientasRequeridas

    return Object.entries(mapa)
      .filter(([, cant]) => cant > 0)
      .map(([id, cant]) => {
        const item = catalogoHerramientas.find((h) => h.id === id)
        return {
          id,
          nombre: item?.nombre ?? 'Herramienta',
          foto: item?.foto ?? null,
          cantidad: cant,
        }
      })
  }, [herramientasAsignadas, herramientasRequeridas, catalogoHerramientas])

  // Reportar avances: como «Terminar» pero sin cerrar. Deja evidencia (fotos +
  // descripción) para retomar la obra al día siguiente. Comparte la captura de
  // fotos (mismo estado `fotos`), porque los dos paneles nunca están abiertos a
  // la vez.
  const [avanzando, setAvanzando] = useState(false)
  const [comentarioAvance, setComentarioAvance] = useState('')

  // El camino de excepción: el teléfono no dio ubicación y hay que explicar
  // por qué. Solo aparece cuando de verdad falló, no antes.
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false)
  const [motivo, setMotivo] = useState('')

  // Pedir más tiempo cuando la orden se cerró sola.
  const [pidiendoTiempo, setPidiendoTiempo] = useState(false)
  const [horasMas, setHorasMas] = useState('2')
  const [justificacion, setJustificacion] = useState('')

  const presentacion = ESTADOS_ORDEN_TRABAJO[trabajo.estado]
  const trabajando = llegar.isPending || salir.isPending

  // ¿La obra es de varios días? Para avisarle de entrada: el primer día reporta
  // avances, no termina. Sale de los mismos bloques de jornada del horario.
  const tramoObra = useMemo(
    () => tramoDeObra(trabajo.fecha_inicio_programada, trabajo.horas_asignadas),
    [trabajo.fecha_inicio_programada, trabajo.horas_asignadas],
  )
  const obraVariosDias =
    tramoObra !== null && tramoObra.dias > 1 && !['cerrada', 'finalizada'].includes(trabajo.estado)

  // Vistas previas de las fotos elegidas. Se crean una vez por foto y se
  // liberan al cambiar o desmontar: crearlas en el render fuga memoria.
  const previews = useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos])
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews])
  const previewsSobrante = useMemo(
    () => fotosSobrante.map((f) => URL.createObjectURL(f)),
    [fotosSobrante],
  )
  useEffect(() => () => previewsSobrante.forEach((u) => URL.revokeObjectURL(u)), [previewsSobrante])

  // Adelanta la carga de Google en cuanto se abre la tarjeta: cuando el técnico
  // llegue a tomar la evidencia, la dirección del sello ya está lista y no se
  // pierde contra el tope de tiempo al primer disparo.
  useEffect(() => {
    precargarGeocodificador()
  }, [])

  // Google Maps con las coordenadas si las hay, y con la dirección escrita si
  // no: el técnico tiene que poder arrancar aunque nadie haya ubicado el sitio.
  const comoLlegar =
    trabajo.latitud !== null && trabajo.longitud !== null
      ? `https://www.google.com/maps/dir/?api=1&destination=${trabajo.latitud},${trabajo.longitud}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          `${trabajo.direccion}, ${trabajo.zona ?? 'Cartago'}, Valle del Cauca, Colombia`,
        )}`

  /** Salir a almorzar: la orden se pausa y lo que sigue hoy se corre. */
  async function salirAAlmorzar() {
    setError(null)
    setAviso(null)
    try {
      const { volver, sinSenal } = await almorzar.mutateAsync()
      setAviso(
        sinSenal
          ? 'Buen provecho. Sin señal: quedó guardado en el teléfono con esta hora y se enviará solo. Al volver a la obra marca «Volver del almuerzo».'
          : `Buen provecho. La orden quedó en pausa y lo que sigue hoy se corrió una hora.${
              volver ? ` Vuelve hacia las ${horaCorta(volver)} y marca «Volver del almuerzo».` : ''
            }`,
      )
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible marcar el almuerzo.')
    }
  }

  /** Llegar. Terminar es aparte porque pide evidencia (confirmarSalida). */
  async function marcarLlegada() {
    setError(null)
    setAviso(null)

    try {
      const { aviso: lejos } = await llegar.mutateAsync()
      setAviso(lejos)
      setPidiendoMotivo(false)
      setMotivo('')
    } catch (fallo) {
      // El teléfono no dio ubicación: no es un error que mostrar y ya, es un
      // camino distinto — hay que decir por qué para poder seguir.
      if (fallo instanceof SinUbicacion) {
        setPidiendoMotivo(true)
        return
      }

      // Está fuera del sitio: es una ADVERTENCIA, no un error rojo. Se muestra
      // en el mismo tono que los avisos y el botón sigue siendo «Llegué».
      if (fallo instanceof FueraDeLaGeocerca) {
        setAviso(fallo.message)
        return
      }

      setError(fallo instanceof Error ? fallo.message : 'No fue posible registrar la marca.')
    }
  }

  /**
   * El sello de la evidencia: orden, fecha y hora, coordenadas, dirección y
   * técnico, quemados en la foto. Una foto sin eso no prueba de qué trabajo es.
   */
  async function sellar(foto: File): Promise<File> {
    const pos = await posicionParaSello()
    const latitud = pos?.coords.latitude ?? trabajo.latitud
    const longitud = pos?.coords.longitude ?? trabajo.longitud

    // La dirección legible del punto, en vez de las coordenadas en grados. Con
    // tope de tiempo para no dejar la foto esperando a Google; si no llega, se
    // estampa con las coordenadas. Nunca lanza.
    let sitioGps: string | null = null
    if (latitud != null && longitud != null) {
      sitioGps = await Promise.race<string | null>([
        direccionDesdeCoords(latitud, longitud).catch(() => null),
        new Promise<null>((r) => setTimeout(() => r(null), 7000)),
      ])
    }

    return estamparFoto(foto, {
      ot: trabajo.numero,
      direccion: trabajo.direccion,
      zona: trabajo.zona,
      tecnico: perfil?.nombre ?? null,
      latitud,
      longitud,
      exactitudM: pos?.coords.accuracy ?? null,
      sitioGps,
    })
  }

  async function agregarFotos(lista: FileList | null, para: 'trabajo' | 'sobrante' = 'trabajo') {
    if (!lista) return
    // Las adjuntadas también se sellan: el acta no distingue de dónde salieron.
    const selladas = await Promise.all(Array.from(lista).map((f) => sellar(f)))
    const poner = para === 'sobrante' ? setFotosSobrante : setFotos
    poner((previas) => [...previas, ...selladas])
  }

  /**
   * Cámara nativa (app). SOLO la cámara: ya no se adjunta de la galería.
   *
   * Una foto de la galería pudo tomarse en otra obra y otro día, y esta
   * evidencia termina en el acta que el cliente firma. Si lo que prueba que se
   * hizo el trabajo puede venir de cualquier parte, no prueba nada.
   */
  async function tomar(desde: 'camara', para: 'trabajo' | 'sobrante' = 'trabajo') {
    // El GPS afina mientras se encuadra (ver calentarUbicacion).
    calentarUbicacion()
    setError(null)
    try {
      const foto = await fotoNativa(desde)
      if (foto) {
        const sellada = await sellar(foto)
        const poner = para === 'sobrante' ? setFotosSobrante : setFotos
        poner((previas) => [...previas, sellada])
      }
    } catch (fallo) {
      // Cancelar la cámara no es un error que valga la pena mostrar.
      const msg = fallo instanceof Error ? fallo.message : ''
      if (!/cancel/i.test(msg)) {
        setError('No se pudo abrir la cámara. Revise que la app tenga el permiso concedido.')
      }
    }
  }

  function quitarFoto(indice: number) {
    setFotos((previas) => previas.filter((_, i) => i !== indice))
  }

  // MEGA exige evidencia real: mínimo 5 fotos para poder terminar la obra.
  const MIN_FOTOS = 5
  const faltanFotos = Math.max(0, MIN_FOTOS - fotos.length)
  const faltaFotoSobrante = sobrantes !== null && sobrantes.length > 0 && fotosSobrante.length === 0
  const listaParaTerminar =
    fotos.length >= MIN_FOTOS &&
    comentarioSalida.trim().length >= 5 &&
    sobrantes !== null &&
    !faltaFotoSobrante

  // Un avance pide menos que un cierre: 3 fotos y la descripción del día.
  const MIN_FOTOS_AVANCE = 3
  const faltanFotosAvance = Math.max(0, MIN_FOTOS_AVANCE - fotos.length)
  const listaParaAvance = fotos.length >= MIN_FOTOS_AVANCE && comentarioAvance.trim().length >= 5

  async function confirmarSalida() {
    setError(null)
    setAviso(null)

    try {
      const horas = await salir.mutateAsync({
        comentario: comentarioSalida.trim(),
        archivos: fotos,
        servicio: trabajo.descripcion ?? trabajo.servicio_nombre ?? null,
        sobrantes: sobrantes ?? [],
        fotosSobrante: sobrantes && sobrantes.length > 0 ? fotosSobrante : [],
      })
      setTerminando(false)
      setFotos([])
      setComentarioSalida('')
      setSobrantes(null)
      setFotosSobrante([])
      // -1: sin señal, quedó en el teléfono con sus fotos (ver useMarcarSalida).
      setAviso(
        horas < 0
          ? 'Sin señal: el cierre y sus fotos quedaron guardados en el teléfono con la hora de ahora. Se envían solos en cuanto haya señal; no borres la app ni cierres sesión.'
          : `Trabajo terminado. Quedaron registradas ${horas} horas y la evidencia.`,
      )
    } catch (fallo) {
      // Estar fuera del sitio es una ADVERTENCIA, no un error: el panel sigue
      // abierto con la foto y el comentario para reintentar al llegar al sitio.
      if (fallo instanceof FueraDeLaGeocerca) {
        setAviso(fallo.message)
        return
      }
      setError(fallo instanceof Error ? fallo.message : 'No fue posible terminar el trabajo.')
    }
  }

  async function confirmarAvance() {
    setError(null)
    setAviso(null)

    try {
      const horas = await avanzar.mutateAsync({
        comentario: comentarioAvance.trim(),
        archivos: fotos,
      })
      setAvanzando(false)
      setFotos([])
      setComentarioAvance('')
      setAviso(
        horas < 0
          ? 'Sin señal: el avance y sus fotos quedaron guardados en el teléfono con la hora de ahora. Se envían solos en cuanto haya señal.'
          : `Avance reportado: ${horas} horas y la evidencia del día. La orden sigue abierta para continuar mañana.`,
      )
    } catch (fallo) {
      // Fuera del sitio es una ADVERTENCIA, no un error: el panel queda abierto
      // con la foto y el comentario para reintentar al llegar al sitio.
      if (fallo instanceof FueraDeLaGeocerca) {
        setAviso(fallo.message)
        return
      }
      setError(fallo instanceof Error ? fallo.message : 'No fue posible reportar el avance.')
    }
  }

  async function pedir() {
    setError(null)

    try {
      await pedirPermiso.mutateAsync(motivo.trim())
      setPidiendoMotivo(false)
      setMotivo('')
      setAviso('Se le pidió permiso al Gerente operativo. Le llega a su tablero.')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible pedir el permiso.')
    }
  }

  async function pedirMasTiempo() {
    setError(null)

    try {
      await pedirTiempo.mutateAsync({
        ordenTrabajoId: trabajo.orden_trabajo_id,
        horas: Number(horasMas),
        justificacion: justificacion.trim(),
      })
      setPidiendoTiempo(false)
      setJustificacion('')
      setAviso('Se pidieron las horas. El Gerente operativo decide.')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible pedir más tiempo.')
    }
  }

  return (
    /*
     * LA TARJETA EN TRES PISOS, NO EN SIETE CAJAS.
     *
     * Johan: «esto se ve muy suelto todo… piensa como diseñador». Eran siete
     * bloques uno debajo del otro, cada uno con su borde y casi el mismo peso
     * —dirección, llamar, aviso azul, reloj en caja roja, Terminar, avances,
     * almuerzo—, y la acción principal quedaba en la mitad, entre avisos.
     *
     * Ahora, de arriba abajo, lo que el técnico se pregunta en ese orden:
     *   1. QUÉ y DÓNDE: el cliente, el trabajo y un solo bloque de lugar con
     *      sus dos acciones (cómo llegar, llamar). El aviso de «obra de varios
     *      días» se volvió una etiqueta junto al estado.
     *   2. CUÁNTO: el reloj como franja de la tarjeta, sin caja propia; el
     *      color está solo en la cifra.
     *   3. QUÉ HAGO: todo junto abajo, donde llega el pulgar. Una principal
     *      grande y, debajo, las secundarias lado a lado y del mismo peso.
     */
    <article
      className={`${estilos.tarjeta} ${enSitio ? estilos.enSitio : ''} ${
        enAlmuerzo ? estilos.enPausa : ''
      }`}
    >
      <header className={estilos.encabezado}>
        <span className={estilos.numero}>{trabajo.numero}</span>
        <span className={estilos.marcas}>
          {obraVariosDias && tramoObra && trabajo.estado !== 'en_precierre' && (
            <span
              className={estilos.chipDias}
              title={`Del ${fechaCorta(tramoObra.desde)} al ${fechaCorta(tramoObra.hasta)}`}
            >
              Obra de {tramoObra.dias} días
            </span>
          )}
          <span
            className={`${estilos.estado} ${
              estilos[enAlmuerzo ? 'en_almuerzo' : trabajo.estado] ?? ''
            }`}
          >
            {enAlmuerzo ? 'En almuerzo' : presentacion?.etiqueta ?? trabajo.estado}
          </span>
        </span>
      </header>

      <div className={estilos.quien}>
        <p className={estilos.cliente}>{trabajo.cliente ?? 'Sin cliente'}</p>
        {(trabajo.servicio_nombre || trabajo.descripcion) && (
          <p className={estilos.queHacer}>
            {trabajo.servicio_codigo && (
              <span className={estilos.codigo}>{trabajo.servicio_codigo}</span>
            )}
            {sinMarcas(trabajo.descripcion) ?? trabajo.servicio_nombre}
          </p>
        )}
      </div>

      {asignados.length > 0 && (
        <div className={estilos.deSobrantes}>
          <p className={estilos.deSobrantesTitulo}>Recoge del inventario de sobrantes</p>
          <ul>
            {asignados.map((a) => (
              <li key={a.id_inventario}>
                <span>{partirDescripcion(a.descripcion).nombre}</span>
                <b>
                  {cantidadLegible(Number(a.cantidad))} {a.unidad ?? ''}
                </b>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 0318: Herramientas requeridas o asignadas para este trabajo */}
      {listaHerramientasTrabajo.length > 0 && (
        <div className={estilos.herramientasOrden}>
          <div className={estilos.herramientasOrdenTitulo}>
            <IconoHerramienta />
            <span>Herramientas para este trabajo</span>
            <span className={estilos.herramientasOrdenCuenta}>
              {listaHerramientasTrabajo.length}
            </span>
          </div>
          <ul className={estilos.herramientasOrdenLista}>
            {listaHerramientasTrabajo.map((h) => (
              <li key={h.id} className={estilos.herramientaItem}>
                {fotoDeHerramienta(h.foto) && (
                  <img
                    className={estilos.herramientaFoto}
                    src={fotoDeHerramienta(h.foto)!}
                    alt=""
                    loading="lazy"
                  />
                )}
                <span className={estilos.herramientaNombre}>{h.nombre}</span>
                <span className={estilos.herramientaCantidad}>×{h.cantidad}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Dónde es, y las dos cosas que se hacen con un «dónde»: ir y llamar.
          Un solo bloque: antes eran dos cajas sueltas. */}
      <div className={estilos.lugar}>
        <div className={estilos.direccion}>
          <span className={estilos.direccionTexto}>{trabajo.direccion}</span>
          {trabajo.zona && <span className={estilos.zona}>{trabajo.zona}</span>}
        </div>
        {trabajo.latitud != null && trabajo.longitud != null && (
          <div style={{ marginTop: '4px', marginBottom: '4px' }}>
            <MapaPreview
              lat={Number(trabajo.latitud)}
              lng={Number(trabajo.longitud)}
              radioM={Number(trabajo.radio_m || 80)}
              direccion={trabajo.direccion}
              alto="150px"
              zoom={16}
            />
          </div>
        )}
        <div className={estilos.lugarAcciones}>
          <a className={estilos.lugarBoton} href={comoLlegar} target="_blank" rel="noreferrer">
            <IconoRuta />
            Cómo llegar
          </a>
          {trabajo.telefono && (
            <a
              className={estilos.lugarBoton}
              href={`tel:${trabajo.telefono}`}
              title={trabajo.telefono}
            >
              <IconoTelefono />
              Llamar
            </a>
          )}
        </div>
      </div>

      {/* El horario del día, solo antes de llegar; ya adentro manda el reloj. */}
      {!enSitio && trabajo.estado === 'programada' && (
        <HorarioObra inicio={trabajo.fecha_inicio_programada} horas={trabajo.horas_asignadas} />
      )}

      <RelojDelTrabajo
        plano
        inicioProgramado={trabajo.fecha_inicio_programada}
        finProgramado={trabajo.fecha_fin_programada}
        horasAsignadas={trabajo.horas_asignadas}
        // En almuerzo el reloj se detiene, aunque el servidor no lo sepa aún.
        llegada={enAlmuerzo ? null : trabajo.llegada}
        terminado={trabajo.estado === 'en_precierre'}
        horasEjecutadas={trabajo.horas_ejecutadas}
        onTerminar={() => setTerminando(true)}
        terminando={salir.isPending}
        // «Terminar» ya no vive en el reloj: va con las demás acciones, abajo.
        puedeTerminar={false}
      />

      {/* En almuerzo: desde cuándo y hasta cuándo. El botón de abajo se
          vuelve «Volver del almuerzo» —es la llegada, con su geocerca—. */}
      {enAlmuerzo && almuerzoDesde && (
        <p className={estilos.enAlmuerzo}>
          <IconoAlmuerzo />
          En almuerzo desde las {horaCorta(almuerzoDesde)} · vuelve hacia las{' '}
          {horaCorta(new Date(new Date(almuerzoDesde).getTime() + 60 * 60_000).toISOString())}
        </p>
      )}

      {/* ── Qué hago ahora: todo junto, abajo ────────────────────────── */}
      {!terminando && !avanzando && (
        <div className={estilos.acciones}>
          {enSitio ? (
            <>
              <button
                type="button"
                className={estilos.principal}
                onClick={() => {
                  setError(null)
                  setAviso(null)
                  setTerminando(true)
                }}
                disabled={salir.isPending}
              >
                {salir.isPending ? 'Registrando…' : 'Terminar'}
              </button>

              {/* Avance para cortar el día y retomar mañana (0177 / 0317) sin cerrar la orden.
                  Almuerzo una vez al día. */}
              <div className={estilos.secundarias}>
                <button
                  type="button"
                  className={estilos.secundaria}
                  onClick={() => {
                    setError(null)
                    setAviso(null)
                    setAvanzando(true)
                  }}
                >
                  <IconoAvance />
                  Reportar avance
                </button>
                {puedeAlmorzar && (
                    <button
                      type="button"
                      className={`${estilos.secundaria} ${estilos.secundariaAlmuerzo}`}
                      onClick={() => void salirAAlmorzar()}
                      disabled={almorzar.isPending}
                    >
                      <IconoAlmuerzo />
                      {almorzar.isPending ? 'Marcando…' : 'Salir a almorzar'}
                    </button>
                  )}
                </div>
            </>
          ) : (
            ['programada', 'en_ejecucion'].includes(trabajo.estado) && (
              <button
                type="button"
                className={estilos.llegue}
                onClick={() => void marcarLlegada()}
                disabled={trabajando}
              >
                {trabajando ? 'Tomando ubicación…' : enAlmuerzo ? 'Volver del almuerzo' : 'Llegué'}
              </button>
            )
          )}
        </div>
      )}

      {aviso && <p className={estilos.aviso}>{aviso}</p>}
      {error && <p className={estilos.error}>{error}</p>}

      {/* ── Terminar: la evidencia antes de cerrar ────────────────────── */}
      {terminando && (
        <div className={estilos.excepcion}>
          <p className={estilos.excepcionTitulo}>Terminar el trabajo</p>
          <p className={estilos.excepcionTexto}>
            Toma al menos una foto de lo hecho y escribe qué se hizo. La evidencia va en el acta.
          </p>

          {/* En la app se usa la cámara NATIVA (el input capture del WebView no
              abre la cámara aunque tenga permiso). En web quedan los <input> de
              archivo de siempre. */}
          {esNativo() ? (
            <div className={estilos.fotoAcciones}>
              <button
                type="button"
                className={estilos.subirFoto}
                onClick={() => void tomar('camara')}
              >
                <IconoCamara />
                Tomar foto
              </button>
            </div>
          ) : (
            <div className={estilos.fotoAcciones}>
              <label className={estilos.subirFoto}>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    agregarFotos(e.target.files)
                    e.target.value = ''
                  }}
                />
                <IconoCamara />
                Tomar foto
              </label>
            </div>
          )}

          {fotos.length > 0 && (
            <div className={estilos.miniaturas}>
              {previews.map((url, i) => (
                <div key={url} className={estilos.miniatura}>
                  <img src={url} alt={`Evidencia ${i + 1}`} />
                  <button
                    type="button"
                    className={estilos.quitarFoto}
                    onClick={() => quitarFoto(i)}
                    aria-label="Quitar foto"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea
            rows={3}
            className={estilos.campo}
            value={comentarioSalida}
            onChange={(e) => setComentarioSalida(e.target.value)}
            placeholder="Qué se hizo: se instaló el tablero, se probó la acometida…"
          />

          <ElegirSobrantes
            materiales={materiales.data ?? []}
            cargando={materiales.isLoading}
            error={materiales.error ? (materiales.error as Error).message : null}
            onCambio={setSobrantes}
            extra={
              <div className={estilos.fotoSobrante}>
                <p className={estilos.fotoSobranteTitulo}>Foto de lo que sobró</p>
                <p className={estilos.fotoSobranteTexto}>
                  Con el material a la vista. Mínimo una: la oficina la mira al recibirlo.
                </p>
                {esNativo() ? (
                  <button
                    type="button"
                    className={estilos.subirFoto}
                    onClick={() => void tomar('camara', 'sobrante')}
                  >
                    <IconoCamara />
                    Foto del sobrante
                  </button>
                ) : (
                  <label className={estilos.subirFoto}>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={(e) => {
                        void agregarFotos(e.target.files, 'sobrante')
                        e.target.value = ''
                      }}
                    />
                    <IconoCamara />
                    Foto del sobrante
                  </label>
                )}
                {fotosSobrante.length > 0 && (
                  <div className={estilos.miniaturas}>
                    {previewsSobrante.map((url, i) => (
                      <div key={url} className={estilos.miniatura}>
                        <img src={url} alt={`Sobrante ${i + 1}`} />
                        <button
                          type="button"
                          className={estilos.quitarFoto}
                          onClick={() => setFotosSobrante((p) => p.filter((_, j) => j !== i))}
                          aria-label="Quitar foto"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            }
          />

          {/* Antes de irse: si alguna herramienta no vuelve con él, que la pase.
              El traslado (a otra orden o a otro técnico) se pide en «Lo que
              llevas encima», más abajo en esta misma pantalla. */}
          <p className={estilos.notaHerramientas}>
            ¿Alguna herramienta pasa a otra orden o a otro técnico? Trasládala en «Lo que llevas
            encima», al final de la pantalla, antes de terminar.
          </p>

          {/* Recordatorio del mínimo, mientras no lo cumpla. */}
          {faltanFotos > 0 && (
            <p className={estilos.faltanFotos}>
              {fotos.length === 0
                ? 'Toma mínimo 5 fotos para poder terminar.'
                : `Van ${fotos.length} de 5 fotos: falta${
                    faltanFotos === 1 ? '' : 'n'
                  } ${faltanFotos}.`}
            </p>
          )}
          {faltanFotos === 0 && sobrantes === null && (
            <p className={estilos.faltanFotos}>Falta decir si sobró material.</p>
          )}
          {faltanFotos === 0 && faltaFotoSobrante && (
            <p className={estilos.faltanFotos}>Falta la foto del material que sobró.</p>
          )}

          <div className={estilos.botones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => {
                setTerminando(false)
                setFotos([])
                setComentarioSalida('')
                setSobrantes(null)
                setFotosSobrante([])
              }}
            >
              Volver
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void confirmarSalida()}
              disabled={!listaParaTerminar || salir.isPending}
            >
              {salir.isPending ? 'Terminando…' : 'Confirmar y terminar'}
            </button>
          </div>
        </div>
      )}

      {/* ── Reportar avances: evidencia sin cerrar la orden ───────────── */}
      {avanzando && (
        <div className={estilos.excepcion}>
          <p className={estilos.excepcionTitulo}>Reportar avances del día</p>
          <p className={estilos.excepcionTexto}>
            Deja fotos de lo avanzado y escribe qué se hizo hoy. La orden NO se cierra: mañana la
            retomas con «Llegué».
          </p>

          {esNativo() ? (
            <div className={estilos.fotoAcciones}>
              <button
                type="button"
                className={estilos.subirFoto}
                onClick={() => void tomar('camara')}
              >
                <IconoCamara />
                Tomar foto
              </button>
            </div>
          ) : (
            <div className={estilos.fotoAcciones}>
              <label className={estilos.subirFoto}>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    agregarFotos(e.target.files)
                    e.target.value = ''
                  }}
                />
                <IconoCamara />
                Tomar foto
              </label>
            </div>
          )}

          {fotos.length > 0 && (
            <div className={estilos.miniaturas}>
              {previews.map((url, i) => (
                <div key={url} className={estilos.miniatura}>
                  <img src={url} alt={`Avance ${i + 1}`} />
                  <button
                    type="button"
                    className={estilos.quitarFoto}
                    onClick={() => quitarFoto(i)}
                    aria-label="Quitar foto"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea
            rows={3}
            className={estilos.campo}
            value={comentarioAvance}
            onChange={(e) => setComentarioAvance(e.target.value)}
            placeholder="Qué se avanzó hoy: se tendió la tubería, falta conectar el equipo…"
          />

          {faltanFotosAvance > 0 && (
            <p className={estilos.faltanFotos}>
              {fotos.length === 0
                ? 'Toma mínimo 3 fotos para reportar el avance.'
                : `Van ${fotos.length} de 3 fotos: falta${
                    faltanFotosAvance === 1 ? '' : 'n'
                  } ${faltanFotosAvance}.`}
            </p>
          )}

          <div className={estilos.botones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => {
                setAvanzando(false)
                setFotos([])
                setComentarioAvance('')
              }}
            >
              Volver
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void confirmarAvance()}
              disabled={!listaParaAvance || avanzar.isPending}
            >
              {avanzar.isPending ? 'Reportando…' : 'Reportar avance'}
            </button>
          </div>
        </div>
      )}

      {/* ── Sin ubicación: lo autoriza otro ───────────────────────────── */}
      {pidiendoMotivo && (
        <div className={estilos.excepcion}>
          <p className={estilos.excepcionTitulo}>El teléfono no dio la ubicación</p>
          <p className={estilos.excepcionTexto}>
            Cuente qué pasó. El Gerente operativo tiene que autorizarlo para que pueda empezar.
          </p>

          <textarea
            rows={3}
            className={estilos.campo}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Sin señal en el sótano, el GPS no engancha…"
          />

          <div className={estilos.botones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => setPidiendoMotivo(false)}
            >
              Volver
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void pedir()}
              disabled={motivo.trim().length < 10 || pedirPermiso.isPending}
            >
              Pedir permiso
            </button>
          </div>
        </div>
      )}

      {trabajo.solicita_inicio && !pidiendoMotivo && (
        <p className={estilos.esperando}>
          Se pidió permiso para empezar sin ubicación. Falta que el Gerente operativo lo autorice.
        </p>
      )}

      {/* ── Se acabó el tiempo ────────────────────────────────────────── */}
      {trabajo.vencida && !trabajo.solicita_mas_tiempo && (
        <div className={estilos.excepcion}>
          <p className={estilos.excepcionTitulo}>Se acabó el tiempo programado</p>
          <p className={estilos.excepcionTexto}>
            La orden se cerró sola. Si todavía le falta, pida las horas explicando por qué.
          </p>

          {pidiendoTiempo ? (
            <>
              <label className={estilos.etiquetaCampo}>
                Cuántas horas más
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  className={estilos.campoNumero}
                  value={horasMas}
                  onChange={(e) => setHorasMas(e.target.value)}
                />
              </label>

              <textarea
                rows={3}
                className={estilos.campo}
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                placeholder="Por qué hizo falta más tiempo"
              />

              <div className={estilos.botones}>
                <button
                  type="button"
                  className={estilos.secundario}
                  onClick={() => setPidiendoTiempo(false)}
                >
                  Volver
                </button>
                <button
                  type="button"
                  className={estilos.principal}
                  onClick={() => void pedirMasTiempo()}
                  disabled={justificacion.trim().length < 10 || pedirTiempo.isPending}
                >
                  Pedir las horas
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className={estilos.principal}
              onClick={() => setPidiendoTiempo(true)}
            >
              Pedir más tiempo
            </button>
          )}
        </div>
      )}

      {trabajo.solicita_mas_tiempo && (
        <p className={estilos.esperando}>
          Ya pidió más tiempo. Falta que el Gerente operativo decida.
        </p>
      )}

      {/* El acta. Es lo último que le queda por vigilar de una orden: la manda
          sola el chatbot al cerrar, y hasta que el cliente no firme el trabajo
          no pasa a servicios finalizados. Si el acta no salió, hasta ahora se
          enteraba semanas después cuando alguien reclamaba. */}
      {['cerrada', 'finalizada'].includes(trabajo.estado) && (
        <div
          className={`${estilos.acta} ${
            trabajo.acta_firmada_en
              ? estilos.actaFirmada
              : trabajo.acta_enviada_en
              ? estilos.actaEnviada
              : estilos.actaSinSalir
          }`}
        >
          <span className={estilos.actaTitulo}>
            {trabajo.acta_firmada_en
              ? 'El cliente firmó el acta'
              : trabajo.acta_enviada_en
              ? 'El acta salió, falta la firma'
              : 'El acta no ha salido'}
          </span>

          <span className={estilos.actaDetalle}>
            {trabajo.acta_firmada_en
              ? `Firmada el ${fechaHora(trabajo.acta_firmada_en)}. Este trabajo ya está cerrado.`
              : trabajo.acta_enviada_en
              ? `Se le envió al cliente el ${fechaHora(trabajo.acta_enviada_en)}.`
              : 'Avísele al Gerente operativo: sin acta el trabajo no se puede dar por terminado.'}
          </span>
        </div>
      )}
    </article>
  )
}
